import React, { useEffect, useState } from "react";import { api, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CalendarX, Plus, Trash, DownloadSimple, FileCsv, FileText, Lock, Sparkle, UserCircle, Bell, Clock, EnvelopeSimple, PaperPlaneTilt, CaretLeft } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ThemeToggle from "@/components/ThemeToggle";
import { toast } from "sonner";

const CURRENCIES = ["TRY", "USD", "EUR", "GBP", "AED", "CHF"];
const HOURS_0_23 = Array.from({ length: 24 }, (_, i) => i);
const REMINDER_MINUTES = [15, 30, 60, 120];
const TAB_LABELS = {
  profile: "Profil",
  password: "Şifre",
  calendar: "Çalışma Saatleri",
  reminder: "Hatırlatma",
  monthly: "Aylık Rapor",
  ai: "AI Asistan",
  email: "E-posta",
  holidays: "Tatiller",
  export: "Veri Aktarma",
};

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    currency: user?.currency || "TRY",
    resend_api_key: user?.resend_api_key || "",
    sender_email: user?.sender_email || "",
    gemini_api_key: user?.gemini_api_key || "",
    calendar_start_hour: user?.calendar_start_hour ?? 7,
    calendar_end_hour: user?.calendar_end_hour ?? 22,
    reminder_enabled: user?.reminder_enabled ?? false,
    reminder_minutes_before: user?.reminder_minutes_before ?? 60,
    reminder_notify_teacher: user?.reminder_notify_teacher ?? true,
    reminder_notify_parent: user?.reminder_notify_parent ?? true,
    monthly_report_auto_send: user?.monthly_report_auto_send ?? false,
    monthly_report_send_day: user?.monthly_report_send_day ?? 1,
  });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("profile");
  const [mobileShowContent, setMobileShowContent] = useState(false);

  // Holidays
  const [holidays, setHolidays] = useState([]);
  const [holForm, setHolForm] = useState({ name: "", start_date: "", end_date: "" });
  const [holLoading, setHolLoading] = useState(false);
  const [holAdding, setHolAdding] = useState(false);

  async function loadHolidays() {
    try { const r = await api.get("/holidays"); setHolidays(r.data); } catch {}
  }
  useEffect(() => { loadHolidays(); }, []);

  async function addHoliday() {
    if (!holForm.name.trim() || !holForm.start_date || !holForm.end_date) {
      return toast.error("Ad, başlangıç ve bitiş tarihi gerekli.");
    }
    setHolAdding(true);
    try {
      const r = await api.post("/holidays", holForm);
      const cancelled = r.data?.cancelled_lessons || 0;
      if (cancelled > 0) {
        toast.success(`Tatil eklendi. ${cancelled} planlı ders iptal edildi.`);
      } else {
        toast.success("Tatil eklendi.");
      }
      setHolForm({ name: "", start_date: "", end_date: "" });
      loadHolidays();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tatil eklenemedi.");
    } finally { setHolAdding(false); }
  }

  async function removeHoliday(id) {
    if (!window.confirm("Bu tatili silmek istiyor musunuz? (İlgili dersler otomatik geri gelmez.)")) return;
    try { await api.delete(`/holidays/${id}`); toast.success("Tatil silindi."); loadHolidays(); }
    catch { toast.error("Silinemedi."); }
  }

  async function seedTurkish() {
    const year = new Date().getFullYear();
    setHolLoading(true);
    try {
      const r = await api.post(`/holidays/seed-turkish?year=${year}`);
      const added = r.data?.added || 0;
      const cancelled = r.data?.cancelled_lessons || 0;
      toast.success(`${added} resmi tatil eklendi${cancelled ? ` · ${cancelled} ders iptal edildi` : ""}.`);
      loadHolidays();
    } catch { toast.error("Eklenemedi."); }
    finally { setHolLoading(false); }
  }

  // ---------- Data Export ----------
  const [exporting, setExporting] = useState(null); // kind or "json" or null

  function triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function exportJson() {
    setExporting("json");
    try {
      const r = await api.get("/export/all");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json;charset=utf-8" });
      const ts = new Date().toISOString().slice(0, 10);
      triggerDownload(blob, `edufin-yedek-${ts}.json`);
      toast.success("Yedek JSON dosyası indirildi.");
    } catch {
      toast.error("Yedek alınamadı.");
    } finally { setExporting(null); }
  }

  async function exportCsv(kind) {
    setExporting(kind);
    try {
      const r = await api.get("/export/csv", { params: { kind }, responseType: "blob" });
      const ts = new Date().toISOString().slice(0, 10);
      triggerDownload(r.data, `edufin-${kind}-${ts}.csv`);
      toast.success("CSV dosyası indirildi.");
    } catch {
      toast.error("Dışa aktarılamadı.");
    } finally { setExporting(null); }
  }

  // ---------- Password change ----------
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  async function changePassword() {
    if (!pw.current_password || !pw.new_password) {
      return toast.error("Mevcut ve yeni şifre gerekli.");
    }
    if (pw.new_password.length < 6) {
      return toast.error("Yeni şifre en az 6 karakter olmalı.");
    }
    if (pw.new_password !== pw.confirm) {
      return toast.error("Yeni şifreler eşleşmiyor.");
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Şifreniz güncellendi.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      const msg = typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail
        : "Şifre değiştirilemedi.";
      toast.error(msg);
    } finally { setPwSaving(false); }
  }

  async function save() {
    setSaving(true);
    try {
      await api.patch("/auth/me", {
        ...form,
        calendar_start_hour: Number(form.calendar_start_hour),
        calendar_end_hour: Number(form.calendar_end_hour),
        reminder_minutes_before: Number(form.reminder_minutes_before),
        monthly_report_send_day: Number(form.monthly_report_send_day),
      });
      await refreshUser();
      toast.success("Ayarlar kaydedildi.");
    } catch { toast.error("Kaydedilemedi."); }
    finally { setSaving(false); }
  }

  const fmtH = (h) => `${String(h).padStart(2, "0")}:00`;

  // Compute slot count & preview for informational text
  const start = Number(form.calendar_start_hour);
  const end = Number(form.calendar_end_hour);
  const slots = end > start ? (end - start) : (24 - start + end);
  const wrapsMidnight = end <= start;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Ayarlar</h1>
        <p className="text-muted-foreground text-sm mt-1">Hesap, takvim ve bildirim tercihleri.</p>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => { setTab(v); setMobileShowContent(true); }}
        orientation="vertical"
        className="flex flex-col md:flex-row gap-4 md:gap-6 items-stretch"
        data-testid="settings-tabs"
      >
        <aside className={`md:w-56 md:flex-shrink-0 ${mobileShowContent ? "hidden md:block" : "block"}`}>
          <div className="md:sticky md:top-4">
            <TabsList className="flex flex-col h-auto p-1.5 bg-card border border-border rounded-lg w-full gap-0.5 items-stretch justify-start">
              <TabsTrigger value="profile" data-testid="settings-tab-profile" className="justify-start gap-2.5 w-full px-3 py-2.5 text-sm rounded-md data-[state=active]:bg-[#3A5A40] data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-[#F1F5F2] hover:text-[#3A5A40] data-[state=active]:hover:bg-[#3A5A40] data-[state=active]:hover:text-white transition-colors text-foreground/80">
                <UserCircle size={16} weight="duotone" />
                <span className="truncate">Profil</span>
              </TabsTrigger>
              <TabsTrigger value="password" data-testid="settings-tab-password" className="justify-start gap-2.5 w-full px-3 py-2.5 text-sm rounded-md data-[state=active]:bg-[#3A5A40] data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-[#F1F5F2] hover:text-[#3A5A40] data-[state=active]:hover:bg-[#3A5A40] data-[state=active]:hover:text-white transition-colors text-foreground/80">
                <Lock size={16} weight="duotone" />
                <span className="truncate">Şifre</span>
              </TabsTrigger>
              <TabsTrigger value="calendar" data-testid="settings-tab-calendar" className="justify-start gap-2.5 w-full px-3 py-2.5 text-sm rounded-md data-[state=active]:bg-[#3A5A40] data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-[#F1F5F2] hover:text-[#3A5A40] data-[state=active]:hover:bg-[#3A5A40] data-[state=active]:hover:text-white transition-colors text-foreground/80">
                <Clock size={16} weight="duotone" />
                <span className="truncate">Çalışma Saatleri</span>
              </TabsTrigger>
              <TabsTrigger value="reminder" data-testid="settings-tab-reminder" className="justify-start gap-2.5 w-full px-3 py-2.5 text-sm rounded-md data-[state=active]:bg-[#3A5A40] data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-[#F1F5F2] hover:text-[#3A5A40] data-[state=active]:hover:bg-[#3A5A40] data-[state=active]:hover:text-white transition-colors text-foreground/80">
                <Bell size={16} weight="duotone" />
                <span className="truncate">Hatırlatma</span>
              </TabsTrigger>
              <TabsTrigger value="monthly" data-testid="settings-tab-monthly" className="justify-start gap-2.5 w-full px-3 py-2.5 text-sm rounded-md data-[state=active]:bg-[#3A5A40] data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-[#F1F5F2] hover:text-[#3A5A40] data-[state=active]:hover:bg-[#3A5A40] data-[state=active]:hover:text-white transition-colors text-foreground/80">
                <PaperPlaneTilt size={16} weight="duotone" />
                <span className="truncate">Aylık Rapor</span>
              </TabsTrigger>
              <TabsTrigger value="ai" data-testid="settings-tab-ai" className="justify-start gap-2.5 w-full px-3 py-2.5 text-sm rounded-md data-[state=active]:bg-[#3A5A40] data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-[#F1F5F2] hover:text-[#3A5A40] data-[state=active]:hover:bg-[#3A5A40] data-[state=active]:hover:text-white transition-colors text-foreground/80">
                <Sparkle size={16} weight="duotone" />
                <span className="truncate">AI Asistan</span>
              </TabsTrigger>
              <TabsTrigger value="email" data-testid="settings-tab-email" className="justify-start gap-2.5 w-full px-3 py-2.5 text-sm rounded-md data-[state=active]:bg-[#3A5A40] data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-[#F1F5F2] hover:text-[#3A5A40] data-[state=active]:hover:bg-[#3A5A40] data-[state=active]:hover:text-white transition-colors text-foreground/80">
                <EnvelopeSimple size={16} weight="duotone" />
                <span className="truncate">E-posta</span>
              </TabsTrigger>
              <TabsTrigger value="holidays" data-testid="settings-tab-holidays" className="justify-start gap-2.5 w-full px-3 py-2.5 text-sm rounded-md data-[state=active]:bg-[#3A5A40] data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-[#F1F5F2] hover:text-[#3A5A40] data-[state=active]:hover:bg-[#3A5A40] data-[state=active]:hover:text-white transition-colors text-foreground/80">
                <CalendarX size={16} weight="duotone" />
                <span className="truncate">Tatiller</span>
              </TabsTrigger>
              <TabsTrigger value="export" data-testid="settings-tab-export" className="justify-start gap-2.5 w-full px-3 py-2.5 text-sm rounded-md data-[state=active]:bg-[#3A5A40] data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-[#F1F5F2] hover:text-[#3A5A40] data-[state=active]:hover:bg-[#3A5A40] data-[state=active]:hover:text-white transition-colors text-foreground/80">
                <DownloadSimple size={16} weight="duotone" />
                <span className="truncate">Veri Aktarma</span>
              </TabsTrigger>
            </TabsList>
          </div>
        </aside>
        <div className={`flex-1 min-w-0 space-y-6 ${mobileShowContent ? "block" : "hidden md:block"}`}>
          <button
            type="button"
            onClick={() => setMobileShowContent(false)}
            className="md:hidden flex items-center gap-1.5 text-sm font-medium text-[#3A5A40] hover:text-[#2E4833] transition-colors -mb-2"
            data-testid="settings-mobile-back"
          >
            <CaretLeft size={16} weight="bold" />
            <span>Tüm ayarlar</span>
            <span className="text-muted-foreground font-normal">· {TAB_LABELS[tab] || ""}</span>
          </button>

      <TabsContent value="profile" className="mt-0 space-y-4 focus-visible:ring-0">
      <div className="bg-white border border-border rounded-lg p-6 space-y-4">
        <h2 className="font-display font-bold text-lg">Profil</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Ad Soyad</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name" />
          </div>
          <div className="space-y-1.5">
            <Label>E-posta</Label>
            <Input value={user?.email || ""} disabled />
          </div>
          <div className="space-y-1.5">
            <Label>Para birimi</Label>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="settings-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Görünüm</Label>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 bg-card">
              <span className="text-xs text-muted-foreground">Açık / Koyu / Sistem</span>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>
      </TabsContent>

      <TabsContent value="password" className="mt-0 space-y-4 focus-visible:ring-0">
      <div className="bg-white border border-border rounded-lg p-6 space-y-4">
        <h2 className="font-display font-bold text-lg flex items-center gap-2">
          <Lock size={20} weight="duotone" className="text-[#3A5A40]" />
          Şifre Değiştir
        </h2>
        <p className="text-sm text-muted-foreground -mt-2">
          Hesap güvenliğiniz için şifrenizi zaman zaman güncelleyin. En az 6 karakter olmalı.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Mevcut şifre</Label>
            <Input
              type="password"
              autoComplete="current-password"
              value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
              data-testid="password-current"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Yeni şifre</Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              data-testid="password-new"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Yeni şifre (tekrar)</Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              data-testid="password-confirm"
            />
          </div>
        </div>
        <div>
          <Button
            onClick={changePassword}
            disabled={pwSaving}
            className="bg-[#3A5A40] hover:bg-[#2E4833] text-white"
            data-testid="password-save"
          >
            {pwSaving ? "Güncelleniyor…" : "Şifreyi güncelle"}
          </Button>
        </div>
      </div>
      </TabsContent>

      <TabsContent value="calendar" className="mt-0 space-y-4 focus-visible:ring-0">
      <div className="bg-white border border-border rounded-lg p-6 space-y-4">
        <div>
          <h2 className="font-display font-bold text-lg">Takvim Çalışma Saatleri</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Takvimde görünecek saat aralığını belirleyin. Örneğin sabah 06:00 - gece 01:00 (gece yarısını geçebilir).
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Başlangıç saati</Label>
            <Select value={String(form.calendar_start_hour)} onValueChange={(v) => setForm({ ...form, calendar_start_hour: Number(v) })}>
              <SelectTrigger data-testid="settings-start-hour"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {HOURS_0_23.map((h) => <SelectItem key={h} value={String(h)}>{fmtH(h)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Bitiş saati</Label>
            <Select value={String(form.calendar_end_hour)} onValueChange={(v) => setForm({ ...form, calendar_end_hour: Number(v) })}>
              <SelectTrigger data-testid="settings-end-hour"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {HOURS_0_23.map((h) => <SelectItem key={h} value={String(h)}>{fmtH(h)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="text-xs bg-muted/40 border border-border rounded-md p-3 text-muted-foreground">
          {slots > 0 ? (
            <>
              Takvim <b>{fmtH(start)}</b>–<b>{fmtH(end === 24 ? 0 : end)}</b> arası gösterilecek
              ({slots} saat{wrapsMidnight ? " · gece yarısı sarmalı" : ""}).
            </>
          ) : (
            <>Bitiş saati başlangıçla aynı olamaz. Lütfen farklı saatler seçin.</>
          )}
        </div>
      </div>
      </TabsContent>

      <TabsContent value="reminder" className="mt-0 space-y-4 focus-visible:ring-0">
      <div className="bg-white border border-border rounded-lg p-6 space-y-4">
        <div>
          <h2 className="font-display font-bold text-lg">Otomatik Ders Hatırlatma</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Ders başlamadan önce size ve/veya veliye otomatik e-posta gönderilir.
            Çalışması için aşağıdaki Resend API anahtarınızın girili olması gerekir.
          </p>
        </div>
        <div className="flex items-center justify-between rounded-md border border-border p-3">
          <div>
            <div className="text-sm font-medium">Otomatik hatırlatmaları aç</div>
            <div className="text-xs text-muted-foreground">Arka planda her dakika dersleri tarar.</div>
          </div>
          <Switch
            checked={!!form.reminder_enabled}
            onCheckedChange={(v) => setForm({ ...form, reminder_enabled: v })}
            data-testid="settings-reminder-toggle"
          />
        </div>
        <div className={`grid sm:grid-cols-3 gap-4 transition-opacity ${form.reminder_enabled ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
          <div className="space-y-1.5">
            <Label>Kaç dakika önce</Label>
            <Select value={String(form.reminder_minutes_before)} onValueChange={(v) => setForm({ ...form, reminder_minutes_before: Number(v) })}>
              <SelectTrigger data-testid="settings-reminder-minutes"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REMINDER_MINUTES.map((m) => (
                  <SelectItem key={m} value={String(m)}>
                    {m >= 60 ? `${m / 60} saat` : `${m} dakika`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border p-3 sm:col-span-1">
            <div className="text-sm">Öğretmene</div>
            <Switch
              checked={!!form.reminder_notify_teacher}
              onCheckedChange={(v) => setForm({ ...form, reminder_notify_teacher: v })}
              data-testid="settings-reminder-teacher"
            />
          </div>
          <div className="flex items-center justify-between rounded-md border border-border p-3 sm:col-span-1">
            <div className="text-sm">Veliye</div>
            <Switch
              checked={!!form.reminder_notify_parent}
              onCheckedChange={(v) => setForm({ ...form, reminder_notify_parent: v })}
              data-testid="settings-reminder-parent"
            />
          </div>
        </div>
      </div>
      </TabsContent>

      <TabsContent value="monthly" className="mt-0 space-y-4 focus-visible:ring-0">
      <div className="bg-white border border-border rounded-lg p-6 space-y-4">
        <div>
          <h2 className="font-display font-bold text-lg">Aylık Otomatik Rapor</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Her ayın belirlenen gününde, bir önceki ayın raporu tüm velilere PDF ek olarak e-posta ile gönderilir.
          </p>
        </div>
        <div className="flex items-center justify-between rounded-md border border-border p-3">
          <div>
            <div className="text-sm font-medium">Ay sonu otomatik rapor</div>
            <div className="text-xs text-muted-foreground">Resend API anahtarı ve velilerin e-postaları gerekli.</div>
          </div>
          <Switch
            checked={!!form.monthly_report_auto_send}
            onCheckedChange={(v) => setForm({ ...form, monthly_report_auto_send: v })}
            data-testid="settings-monthly-auto"
          />
        </div>
        <div className={`grid sm:grid-cols-2 gap-4 transition-opacity ${form.monthly_report_auto_send ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
          <div className="space-y-1.5">
            <Label>Gönderim günü (ayın kaçıncısı)</Label>
            <Select value={String(form.monthly_report_send_day)} onValueChange={(v) => setForm({ ...form, monthly_report_send_day: Number(v) })}>
              <SelectTrigger data-testid="settings-monthly-day"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <SelectItem key={d} value={String(d)}>{d}. gün</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Örn: "1. gün" → her ay 1'inde, bir önceki ayın raporu.</p>
          </div>
        </div>
      </div>
      </TabsContent>

      <TabsContent value="ai" className="mt-0 space-y-4 focus-visible:ring-0">
      <div className="bg-white border border-border rounded-lg p-6 space-y-4">
        <div>
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <Sparkle size={20} weight="duotone" className="text-[#3A5A40]" />
            AI Asistan (Google Gemini)
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Sağ alt köşedeki sohbet butonuyla "Bugün hangi derslerim var?", "Bu ay kim ödedi?" gibi sorular sorabilirsin.
            <br />
            <strong>Tamamen ücretsiz:</strong>{" "}
            <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" className="text-[#3A5A40] underline font-medium">
              aistudio.google.com/apikey
            </a>{" "}
            adresinden Google hesabınla giriş yap → "Create API key" → kopyala → aşağıya yapıştır. Günde 1.000 ücretsiz istek hakkın olur.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Gemini API Anahtarı</Label>
          <Input
            type="password"
            placeholder="AIza..."
            value={form.gemini_api_key}
            onChange={(e) => setForm({ ...form, gemini_api_key: e.target.value })}
            data-testid="settings-gemini-key"
          />
        </div>
      </div>
      </TabsContent>

      <TabsContent value="email" className="mt-0 space-y-4 focus-visible:ring-0">
      <div className="bg-white border border-border rounded-lg p-6 space-y-4">
        <div>
          <h2 className="font-display font-bold text-lg">E-posta Bildirimleri (Resend)</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Veliye ders onayı / hatırlatma e-postaları için Resend API anahtarınızı girin.
            Anahtar almak için: <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-[#3A5A40] underline">resend.com</a>
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Resend API Anahtarı</Label>
          <Input type="password" placeholder="re_..." value={form.resend_api_key} onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })} data-testid="settings-resend-key" />
        </div>
        <div className="space-y-1.5">
          <Label>Gönderen e-posta (from)</Label>
          <Input placeholder="onboarding@resend.dev" value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} />
        </div>
      </div>
      </TabsContent>

      <TabsContent value="holidays" className="mt-0 space-y-4 focus-visible:ring-0">
      <div className="bg-white border border-border rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <CalendarX size={20} weight="duotone" className="text-[#D97757]" />
              Kapalı Günler / Tatiller
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Bu tarih aralıklarına denk gelen tekrarlayan dersler otomatik atlanır. Eklediğinizde mevcut planlı dersler de iptal edilir.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={seedTurkish}
            disabled={holLoading}
            data-testid="seed-tr-holidays"
          >
            {holLoading ? "Ekleniyor…" : `${new Date().getFullYear()} resmi tatillerini ekle`}
          </Button>
        </div>

        {/* Add form */}
        <div className="grid sm:grid-cols-[1fr_150px_150px_auto] gap-2 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs">Ad</Label>
            <Input
              placeholder="Örn: Yarıyıl tatili"
              value={holForm.name}
              onChange={(e) => setHolForm({ ...holForm, name: e.target.value })}
              data-testid="holiday-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Başlangıç</Label>
            <Input
              type="date"
              value={holForm.start_date}
              onChange={(e) => setHolForm({ ...holForm, start_date: e.target.value, end_date: holForm.end_date || e.target.value })}
              data-testid="holiday-start"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Bitiş</Label>
            <Input
              type="date"
              value={holForm.end_date}
              onChange={(e) => setHolForm({ ...holForm, end_date: e.target.value })}
              data-testid="holiday-end"
            />
          </div>
          <Button
            onClick={addHoliday}
            disabled={holAdding}
            className="bg-[#D97757] hover:bg-[#BF6446] text-white"
            data-testid="holiday-add"
          >
            <Plus size={14} className="mr-1" /> Ekle
          </Button>
        </div>

        {/* List */}
        {holidays.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-4 border border-dashed border-border rounded-md">
            Henüz tanımlı tatil yok.
          </div>
        ) : (
          <ul className="divide-y divide-border border border-border rounded-md overflow-hidden">
            {holidays.map((h) => (
              <li key={h.id} className="flex items-center gap-3 p-3 bg-[#FAF9F6]" data-testid={`holiday-${h.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate text-sm">{h.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatDate(h.start_date)} – {formatDate(h.end_date)}
                  </div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => removeHoliday(h.id)} className="h-8 w-8 text-[#D97757]" data-testid={`holiday-delete-${h.id}`}>
                  <Trash size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      </TabsContent>

      <TabsContent value="export" className="mt-0 space-y-4 focus-visible:ring-0">
      <div className="bg-white border border-border rounded-lg p-6 space-y-4">
        <div>
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <DownloadSimple size={20} weight="duotone" className="text-[#3A5A40]" />
            Veri Yedekleme ve Dışa Aktarma
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Tüm verilerinizi JSON olarak yedekleyebilir veya tek tek tabloları Excel'de açılabilen CSV olarak indirebilirsiniz.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={exportJson}
            disabled={exporting === "json"}
            className="bg-[#3A5A40] hover:bg-[#2E4833] text-white"
            data-testid="export-json-button"
          >
            <FileText size={16} weight="duotone" className="mr-1.5" />
            {exporting === "json" ? "Hazırlanıyor…" : "Tümünü JSON olarak indir"}
          </Button>
        </div>
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">CSV Dışa Aktarma</div>
          <div className="flex flex-wrap gap-2">
            {[
              { k: "students", label: "Öğrenciler" },
              { k: "lessons", label: "Dersler" },
              { k: "payments", label: "Ödemeler" },
              { k: "expenses", label: "Giderler" },
              { k: "holidays", label: "Tatiller" },
            ].map((o) => (
              <Button
                key={o.k}
                variant="outline"
                size="sm"
                onClick={() => exportCsv(o.k)}
                disabled={exporting === o.k}
                data-testid={`export-csv-${o.k}`}
              >
                <FileCsv size={14} weight="duotone" className="mr-1.5" />
                {exporting === o.k ? "…" : o.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
      </TabsContent>

      </div>
      </Tabs>

      <div className="sticky bottom-0 left-0 right-0 -mx-2 px-2 py-3 bg-gradient-to-t from-background via-background/95 to-transparent flex justify-end z-10">
        <Button onClick={save} disabled={saving || slots === 0} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white shadow-lg" data-testid="settings-save-button">
          {saving ? "Kaydediliyor…" : "Tüm değişiklikleri kaydet"}
        </Button>
      </div>
    </div>
  );
}
